import { all, call, put, takeLatest } from "redux-saga/effects";
import Swal from "sweetalert2";
import axios from "axios";
import i18n from "../../i18n/i18n";

import {
    GET_MY_AVANCE_REQUESTS_REQUEST,
    GET_MY_AVANCES_EN_COURS_REQUEST,
    CREATE_AVANCE_REQUEST_REQUEST,
    CANCEL_AVANCE_REQUEST_REQUEST,
    GET_MANAGER_AVANCE_INBOX_REQUEST,
    MANAGER_AVANCE_DECISION_REQUEST,
    GET_MANAGER_AVANCE_HISTORY_REQUEST,
    GET_ADMIN_AVANCE_INBOX_REQUEST,
    ADMIN_AVANCE_DECISION_REQUEST,
    GET_ADMIN_AVANCE_HISTORY_REQUEST,
    ENREGISTRER_REMBOURSEMENT_REQUEST,
    REMBOURSEMENT_ANTICIPE_REQUEST,
    EXPORT_AVANCE_REQUESTS_EXCEL_REQUEST,

    setAvanceRequestLoading,
    getMyAvanceRequestsSuccess,
    getMyAvanceRequestsFail,
    getMyAvancesEnCoursSuccess,
    getMyAvancesEnCoursFail,
    createAvanceRequestSuccess,
    createAvanceRequestFail,
    cancelAvanceRequestSuccess,
    cancelAvanceRequestFail,
    getManagerAvanceInboxSuccess,
    getManagerAvanceInboxFail,
    managerAvanceDecisionSuccess,
    managerAvanceDecisionFail,
    getManagerAvanceHistorySuccess,
    getManagerAvanceHistoryFail,
    getAdminAvanceInboxSuccess,
    getAdminAvanceInboxFail,
    adminAvanceDecisionSuccess,
    adminAvanceDecisionFail,
    getAdminAvanceHistorySuccess,
    getAdminAvanceHistoryFail,
    enregistrerRemboursementSuccess,
    enregistrerRemboursementFail,
    remboursementAnticipeSuccess,
    remboursementAnticipeFail,
    exportAvanceRequestsExcelSuccess,
    exportAvanceRequestsExcelFail,
} from "../actions/avanceRequestActions";

import { API_BASE_URL, getRequest, postRequest } from "../../../api";

const extractErrorMessage = (error, fallback) => {
    const data = error?.response?.data;

    if (data?.message) return String(data.message);
    if (data?.error) return String(data.error);
    if (error?.message) return String(error.message);

    try {
        if (data && typeof data === "object") return JSON.stringify(data);
    } catch {}
    return fallback;
};

// ===================== WORKERS =====================

export function* getMyAvanceRequestsSaga(action) {
    const url = `${API_BASE_URL}/api/avance-requests/requests/mine`;
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const silent = action?.meta?.silent === true;
    if (!silent) yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(getRequest, { url, token, headers: { uid } });
        yield put(getMyAvanceRequestsSuccess(resp.data || []));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur chargement demandes d'avance");
        yield put(getMyAvanceRequestsFail(msg));
    } finally {
        if (!silent) yield put(setAvanceRequestLoading(false));
    }
}

export function* getMyAvancesEnCoursSaga(action) {
    const url = `${API_BASE_URL}/api/avance-requests/requests/mine/en-cours`;
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const silent = action?.meta?.silent === true;
    if (!silent) yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(getRequest, { url, token, headers: { uid } });
        yield put(getMyAvancesEnCoursSuccess(resp.data || []));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur chargement avances en cours");
        yield put(getMyAvancesEnCoursFail(msg));
    } finally {
        if (!silent) yield put(setAvanceRequestLoading(false));
    }
}

export function* createAvanceRequestSaga(action) {
    const url = `${API_BASE_URL}/api/avance-requests/requests`;
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    yield put(setAvanceRequestLoading(true));
    try {
        const payload = action.payload;

        console.log("📤 Payload reçu:", payload);

        // ✅ ÉTAPE 1 : Créer la demande (JSON simple)
        const requestData = {
            avanceId: payload.avanceId,
            montantDemande: payload.montantDemande,
            nombreMensualites: payload.nombreMensualites,
            requestType: payload.requestType,
            motif: payload.motif || null,
            urgent: payload.urgent || false,
            echeance: payload.echeance,
        };

        console.log("📦 Données de la demande:", requestData);

        const resp = yield call(postRequest, {
            url,
            token,
            data: requestData,
            headers: { uid },
        });

        console.log("✅ Demande créée:", resp.data);

        // ✅ ÉTAPE 2 : Upload fichiers si présents
        if (payload.justificatifs && Array.isArray(payload.justificatifs) && payload.justificatifs.length > 0) {
            const validFiles = payload.justificatifs.filter(file =>
                file instanceof File && file.size > 0
            );

            if (validFiles.length > 0) {
                console.log("📎 Upload de", validFiles.length, "fichiers...");

                const uploadUrl = `${API_BASE_URL}/api/avance-requests/requests/${resp.data.id}/justificatifs`;
                const formData = new FormData();

                validFiles.forEach(file => {
                    formData.append("files", file);
                });

                yield call(postRequest, {
                    url: uploadUrl,
                    token,
                    data: formData,
                    headers: { uid },
                });

                console.log("✅ Fichiers uploadés");
            }
        }

        yield put(createAvanceRequestSuccess(resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "Succès",
            text: "Demande d'avance envoyée avec succès",
            timer: 1800,
            showConfirmButton: false,
        });

        yield put({ type: GET_MY_AVANCE_REQUESTS_REQUEST });

    } catch (error) {
        console.error("❌ Erreur création demande:", error);
        console.error("❌ Response:", error?.response?.data);
        console.error("❌ Status:", error?.response?.status);

        const msg = extractErrorMessage(error, "Erreur création demande d'avance");
        const translatedMsg = i18n.t(msg, { defaultValue: msg });
        yield put(createAvanceRequestFail(translatedMsg));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setAvanceRequestLoading(false));
    }
}

export function* cancelAvanceRequestSaga(action) {
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");
    const { id } = action.payload || {};
    const url = `${API_BASE_URL}/api/avance-requests/requests/${id}/cancel`;

    yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(postRequest, { url, token, headers: { uid } });
        yield put(cancelAvanceRequestSuccess(resp.data));
        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Demande d'avance annulée avec succès",
            timer: 1800,
            showConfirmButton: false,
        });
        yield put({ type: GET_MY_AVANCE_REQUESTS_REQUEST });
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur annulation demande d'avance");
        const translatedMsg = i18n.t(msg, { defaultValue: msg });
        yield put(cancelAvanceRequestFail(translatedMsg));
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: i18n.t("Erreur"),
            text: translatedMsg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setAvanceRequestLoading(false));
    }
}

export function* getManagerAvanceInboxSaga(action) {
    const url = `${API_BASE_URL}/api/avance-requests/manager/inbox`;
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const silent = action?.meta?.silent === true;
    if (!silent) yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(getRequest, { url, token, headers: { uid } });
        yield put(getManagerAvanceInboxSuccess(resp.data || []));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur chargement inbox manager");
        yield put(getManagerAvanceInboxFail(msg));
    } finally {
        if (!silent) yield put(setAvanceRequestLoading(false));
    }
}

export function* managerAvanceDecisionSaga(action) {
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const { id, decision, comment } = action.payload || {};
    const url = `${API_BASE_URL}/api/avance-requests/manager/requests/${id}/decision`;

    yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(postRequest, {
            url,
            token,
            data: { decision, comment },
            headers: { uid },
        });

        yield put(managerAvanceDecisionSuccess(resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Décision envoyée",
            timer: 1400,
            showConfirmButton: false,
        });

        yield put({ type: GET_MANAGER_AVANCE_INBOX_REQUEST });
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur décision manager");
        yield put(managerAvanceDecisionFail(msg));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setAvanceRequestLoading(false));
    }
}

// ✅ AJOUT: Saga pour l'historique manager
export function* getManagerAvanceHistorySaga(action) {
    const url = `${API_BASE_URL}/api/avance-requests/manager/history`;
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const silent = action?.meta?.silent === true;
    if (!silent) yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(getRequest, { url, token, headers: { uid } });
        yield put(getManagerAvanceHistorySuccess(resp.data || []));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur chargement historique manager");
        yield put(getManagerAvanceHistoryFail(msg));
    } finally {
        if (!silent) yield put(setAvanceRequestLoading(false));
    }
}

export function* getAdminAvanceInboxSaga(action) {
    const url = `${API_BASE_URL}/api/avance-requests/admin/inbox`;
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const silent = action?.meta?.silent === true;
    if (!silent) yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(getRequest, { url, token, headers: { uid } });
        yield put(getAdminAvanceInboxSuccess(resp.data || []));
    } catch (error) {
        yield put(getAdminAvanceInboxFail(extractErrorMessage(error, "Erreur chargement inbox RH")));
    } finally {
        if (!silent) yield put(setAvanceRequestLoading(false));
    }
}

export function* adminAvanceDecisionSaga(action) {
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const { id, decision, comment } = action.payload || {};
    const url = `${API_BASE_URL}/api/avance-requests/admin/requests/${id}/decision`;

    yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(postRequest, {
            url,
            token,
            data: { decision, comment },
            headers: { uid },
        });

        yield put(adminAvanceDecisionSuccess(resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Décision RH envoyée",
            timer: 1400,
            showConfirmButton: false,
        });

        yield put({ type: GET_ADMIN_AVANCE_INBOX_REQUEST });
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur décision RH");
        yield put(adminAvanceDecisionFail(msg));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setAvanceRequestLoading(false));
    }
}

export function* getAdminAvanceHistorySaga(action) {
    const url = `${API_BASE_URL}/api/avance-requests/admin/history`;
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const silent = action?.meta?.silent === true;
    if (!silent) yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(getRequest, { url, token, headers: { uid } });
        yield put(getAdminAvanceHistorySuccess(resp.data || []));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur chargement historique admin");
        yield put(getAdminAvanceHistoryFail(msg));
    } finally {
        if (!silent) yield put(setAvanceRequestLoading(false));
    }
}

export function* enregistrerRemboursementSaga(action) {
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const { id, ...remboursementData } = action.payload || {};
    const url = `${API_BASE_URL}/api/avance-requests/admin/requests/${id}/remboursement`;

    yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(postRequest, {
            url,
            token,
            data: remboursementData,
            headers: { uid },
        });

        yield put(enregistrerRemboursementSuccess(resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Remboursement enregistré",
            timer: 1400,
            showConfirmButton: false,
        });

        yield put({ type: GET_MY_AVANCES_EN_COURS_REQUEST });
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur enregistrement remboursement");
        yield put(enregistrerRemboursementFail(msg));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setAvanceRequestLoading(false));
    }
}

export function* remboursementAnticipeSaga(action) {
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const { id } = action.payload || {};
    const url = `${API_BASE_URL}/api/avance-requests/requests/${id}/remboursement-anticipe`;

    yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(postRequest, {
            url,
            token,
            headers: { uid },
        });

        yield put(remboursementAnticipeSuccess(resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Remboursement anticipé effectué",
            timer: 1400,
            showConfirmButton: false,
        });

        yield put({ type: GET_MY_AVANCES_EN_COURS_REQUEST });
        yield put({ type: GET_MY_AVANCE_REQUESTS_REQUEST });
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur remboursement anticipé");
        yield put(remboursementAnticipeFail(msg));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setAvanceRequestLoading(false));
    }
}

export function* exportAvanceRequestsExcelSaga(action) {
    const source = action.payload?.source || "admin_inbox";
    const url = `${API_BASE_URL}/api/avance-requests/export-excel?source=${source}`;
    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    yield put(setAvanceRequestLoading(true));
    try {
        const resp = yield call(axios.get, url, {
            responseType: "blob",
            headers: {
                Authorization: `Bearer ${token}`,
                uid,
            },
        });

        const blob = new Blob([resp.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `demandes_avances_${source}_${new Date().getTime()}.xlsx`;
        link.click();

        yield put(exportAvanceRequestsExcelSuccess());
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur export Excel");
        yield put(exportAvanceRequestsExcelFail(msg));
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: msg,
        });
    } finally {
        yield put(setAvanceRequestLoading(false));
    }
}
